import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  MenuItem,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Divider,
} from '@mui/material'
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material'
import api from '../services/api'

interface Role {
  id: string
  name: string
  description: string
  permissions_count: number
}

interface FormData {
  email: string
  password: string
  full_name: string
  role_id: string
  is_active: boolean
}

const AdminUserFormPage: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditMode = Boolean(id)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [roles, setRoles] = useState<Role[]>([])
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)

  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    full_name: '',
    role_id: '',
    is_active: true,
  })

  useEffect(() => {
    loadRoles()
    if (isEditMode && id) {
      loadUser(id)
    }
  }, [id, isEditMode])

  const loadRoles = async () => {
    try {
      const response = await api.get('/admin/system/roles')
      setRoles(response.data)
    } catch (err) {
      console.error('Failed to load roles:', err)
    }
  }

  const loadUser = async (userId: string) => {
    try {
      setLoading(true)
      const response = await api.get(`/admin/admin-users/${userId}`)
      const user = response.data
      
      setFormData({
        email: user.email,
        password: '', // Don't load password
        full_name: user.full_name,
        role_id: user.role_id,
        is_active: user.is_active,
      })
      
      // Set selected role for display
      const role = roles.find(r => r.id === user.role_id)
      if (role) setSelectedRole(role)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load user')
    } finally {
      setLoading(false)
    }
  }

  const handleRoleChange = (roleId: string) => {
    setFormData({ ...formData, role_id: roleId })
    const role = roles.find(r => r.id === roleId)
    setSelectedRole(role || null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!formData.email || !formData.full_name || !formData.role_id) {
      setError('Please fill in all required fields')
      return
    }

    if (!isEditMode && !formData.password) {
      setError('Password is required for new users')
      return
    }

    if (!isEditMode && formData.password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    try {
      setLoading(true)

      if (isEditMode) {
        // Update existing user
        await api.put(`/admin/admin-users/${id}`, {
          full_name: formData.full_name,
          role_id: formData.role_id,
          is_active: formData.is_active,
        })
      } else {
        // Create new user
        await api.post('/admin/admin-users', formData)
      }

      navigate('/admin/users')
    } catch (err: any) {
      setError(err.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'create'} user`)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/admin/users')
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEditMode ? 'Edit Admin User' : 'Create Admin User'}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {isEditMode
          ? 'Update admin user details and role assignment'
          : 'Create a new admin user with role-based permissions'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mt: 2, mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mt: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Full Name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email Address"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={loading || isEditMode}
                helperText={isEditMode ? 'Email cannot be changed' : ''}
              />
            </Grid>

            {!isEditMode && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  type="password"
                  label="Password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  disabled={loading}
                  helperText="Minimum 8 characters"
                />
              </Grid>
            )}

            {/* Role Assignment */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Role Assignment
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                select
                label="Role"
                value={formData.role_id}
                onChange={(e) => handleRoleChange(e.target.value)}
                disabled={loading}
                helperText="Select the role to assign to this user"
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {selectedRole && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Role Details
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      {selectedRole.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {selectedRole.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedRole.permissions_count} permissions assigned
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Status */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Status
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    disabled={loading}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Active</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formData.is_active
                        ? 'User can log in and access the system'
                        : 'User is deactivated and cannot log in'}
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : isEditMode ? 'Update User' : 'Create User'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {isEditMode && (
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Password Management
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            To change the user's password, use the "Change Password" button on the users list page.
          </Typography>
        </Paper>
      )}
    </Box>
  )
}

export default AdminUserFormPage
